import { Footer } from '@/layouts/Footer';
import TopNav from '@/layouts/TopNav';
import HeroArchitecture from '@/views/ai-home/HeroArchitecture';
import McpChatDemo from '@/views/ai-home/demos/McpChatDemo';
import SystemArchitecture from './SystemArchitecture';
import HeroHexes from './HeroHexes';
import PublicDataExpertise from './PublicDataExpertise';
import { DataShowcase, CtaBand } from './MainSections';
import Seo from '@/components/seo/Seo';
import { ROUTE_SEO } from '@/config/seo';

/**
 * 통합 메인(홈) — 공공데이터 + AI 를 한 화면에 담는 정식 메인 페이지.
 * 구성:
 *  - 히어로: 카피 + 통합 시스템 아키텍처(소스→공공데이터/공공서비스→AI) + 두 기둥
 *  - 공공데이터: 운영·개방 전문성(국가중점데이터 개방 국내 최다 실적)
 *  - AI PLATFORM: AI 플랫폼 아키텍처 + 라이브 채팅
 *  - 공공데이터·공공서비스(PUBLIC DATA · SERVICE)
 *  - CTA
 */
export default function MainIndex() {
  return (
    <div className="mh mh-v1">
      <Seo path={ROUTE_SEO.main.path} description={ROUTE_SEO.main.description} />
      <TopNav variant="light" />

      <header className="mh-hero">
        {/* 헥사곤 모티프(정적) — 헤더 첫 자식 */}
        <HeroHexes />
        {/* 배경 분위기 — 미세 그리드 + 소프트 스크림 + 브랜드 블롭 */}
        <div className="mh-hero__bg" aria-hidden>
          <span className="mh-hero__grid" />
          {/* 텍스트 영역을 덮는 소프트 스크림 — 격자/데코를 흐려 가독성 확보 */}
          <span className="mh-hero__scrim" />
          <span className="mh-hero__blob mh-hero__blob--1" />
          <span className="mh-hero__blob mh-hero__blob--2" />
        </div>
        <div className="mh__container">
          <div className="mh-herohead">
            <div className="mh-hero__inner">
              <span className="mh-eyebrow">PUBLIC DATA × AI</span>
              <h1 className="mh-hero__title">
                공공데이터 <span className="mh-grad-red">개방</span>에서 <span className="mh-grad-red">AI</span>까지
                <br />
                하나로 연결합니다
              </h1>
              <div className="mh-hero__rule" />
              <p className="mh-hero__sub">
                프람트테크놀로지는 공공데이터의 개방·구축·품질 고도화부터 LLM 기반 AI 플랫폼까지, 데이터의 전 여정을
                설계하고 운영하며 <b>공공혁신의 플랫폼</b>을 만듭니다.
              </p>
            </div>

            <div className="mh-hero__viz" aria-hidden>
              <SystemArchitecture />
            </div>
          </div>

          <div className="mh-pillars">
            <a className="mh-pill" href="/data">
              <span className="mh-pill__tag">PUBLIC DATA</span>
              <h2 className="mh-pill__title">공공데이터</h2>
              <p className="mh-pill__desc">
                데이터 개방·구축·정제·품질 검증까지. 공공의 데이터를 신뢰할 수 있는 자산으로 만듭니다.
              </p>
              <div className="mh-pill__chips">
                <span className="mh-pill__chip">Data Infuser</span>
                <span className="mh-pill__chip">OpenAPI</span>
                <span className="mh-pill__chip">Data Verifier</span>
                <span className="mh-pill__chip">API 품질</span>
              </div>
              <span className="mh-cta mh-pill__more">자세히 보기 →</span>
            </a>

            <a className="mh-pill" href="/ai">
              <span className="mh-pill__tag">AI PLATFORM</span>
              <h2 className="mh-pill__title">AI</h2>
              <p className="mh-pill__desc">
                공공 MCP 서버 구축. LLM·RAG·AI Agent로 공공서비스를 지능형으로 혁신합니다.
              </p>
              <div className="mh-pill__chips">
                <span className="mh-pill__chip">AI Agent</span>
                <span className="mh-pill__chip">MCP Server</span>
                <span className="mh-pill__chip">Agent2Agent</span>
                <span className="mh-pill__chip">AgentGateway</span>
                <span className="mh-pill__chip">RAG</span>
              </div>
              <span className="mh-cta mh-pill__more">자세히 보기 →</span>
            </a>
          </div>
        </div>
      </header>

      {/* 공공데이터 — 운영·개방 전문성(신규) */}
      <section className="mh-section mh-section--soft">
        <div className="mh-blob mh--grey" style={{ width: 320, height: 320, left: -80, top: 40 }} />
        <div className="mh__container">
          <PublicDataExpertise />
        </div>
      </section>

      {/* AI PLATFORM — AI 플랫폼 아키텍처 + 채팅 */}
      <section className="mh-section">
        <div className="mh__container">
          <div className="mh-shead mh-shead--center">
            <span className="mh-eyebrow">AI PLATFORM</span>
            <h2 className="mh-shead__title">AI 기반 공공서비스 제공 체계</h2>
            <p className="mh-shead__lead">
              공공데이터·공공서비스를 표준 게이트웨이로 안전하게 연결하고, 자연어 질의가 도구 호출을 거쳐 실시간으로
              응답하는 과정을 보여줍니다.
            </p>
          </div>

          <div className="mh-ai">
            <div className="mh-ai__grid">
              <div className="mh-ai__chat">
                <McpChatDemo />
              </div>
              <div className="mh-ai__arch">
                <HeroArchitecture />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 공공데이터 · 공공서비스(PUBLIC DATA · SERVICE) */}
      <section className="mh-section mh-section--soft">
        <div className="mh-blob mh--grey" style={{ width: 320, height: 320, left: -80, top: 40 }} />
        <div className="mh__container">
          <DataShowcase />
        </div>
      </section>

      {/* CTA */}
      <section className="mh-section">
        <div className="mh__container">
          <CtaBand />
        </div>
      </section>

      <Footer />
    </div>
  );
}
